import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Loader2, PlayCircle, Send, Settings, Sparkles } from 'lucide-react';

/**
 * Bookkeeping → **CA agent** (Business Rule #55) — the one place the accounting agent talks to the admin.
 *
 * Four blocks, little text (owner: "make it simple and minimal"): **Needs you** — the open issues, each with
 * its one-tap answers and an Open link into the tab that holds the fix; **Chat** — the agent's questions,
 * reminders, digests and the developer agent's notes, with tap-options on anything unanswered and a box to type
 * in (Enter sends; a typed reply goes to the last open question); **Due** — what is payable or filable in the
 * next weeks with the amount the books compute and Paid / Filed buttons; **Decisions** folded away. ⚙ opens
 * the setup sheet (who to talk to, when, how much to do alone, which obligations). Every figure is the server's;
 * this page computes nothing but which button a row shows. Polls the chat every 20 s while mounted.
 *
 * @param {{ token: string, month: string, onAction?: Function }} props
 */
const API = () => `${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/ca-agent`;
const POLL_MS = 20000;
const n0 = (v) => Math.round(Number(v || 0)).toLocaleString('en-IN');
const inr = (v) => (v == null ? '—' : `₹${n0(v)}`);
const day = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—');
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const AUTHOR = { runtime: 'CA agent', developer: 'Developer agent', fetcher: 'Portal fetcher', owner: 'You' };
const WHO = { us: 'we pay', ca: 'CA files', consultant: 'consultant files' };
const TONE = { red: 'bg-red-50 text-red-700', amber: 'bg-amber-50 text-amber-800', green: 'bg-emerald-50 text-emerald-700', grey: 'bg-[hsl(30_6%_95%)] text-[hsl(0_0%_35%)]', sky: 'bg-sky-50 text-sky-700' };
const PLAYBOOK_LABEL = { fft_bill: 'FFT partner bill', retainer: 'Aasma retainer', tds_knockoff: 'TDS knock-off (CA clearing)', accruals: 'Utility & ice accruals', salary_alloc: 'Salary allocation', vihar: 'Vihar warehouse bills', provision: 'Purchase provision', expense_bill_post: 'Post a supplier bill', record_bill_payment: 'Record a Kotak payment' };
const MAIL_LABEL = { ca_pack: 'Monthly pack to the CA', consultant_pack: 'Monthly note to the labour consultant', ca_query: 'Questions to the CA', consultant_query: 'Questions to the consultant' };

/** Pure: the pill tone of an obligation row — red overdue / due today, amber within 3 days, grey else. */
export function dueTone(inst, today) {
  if (!inst?.due_date) return 'grey';
  if (inst.state === 'at_risk') return 'red';
  const d = Math.round((new Date(`${inst.due_date}T00:00:00`) - new Date(`${today}T00:00:00`)) / 86400000);
  if (d <= 0) return 'red';
  if (d <= 3) return 'amber';
  return 'grey';
}

/** Pure: the text under a chat message's author line. */
export function messageMeta(m) {
  const bits = [AUTHOR[m.author] || AUTHOR[m.role] || 'CA agent', when(m.created_at)];
  if (m.kind && !['info', 'reply', 'command', 'result'].includes(m.kind)) bits.push(m.kind);
  return bits.join(' · ');
}

function Pill({ tone = 'grey', children, testId }) {
  if (!children) return null;
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${TONE[tone] || TONE.grey}`} data-testid={testId}>{children}</span>;
}

function Panel({ title, sub, right, children, testId }) {
  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid={testId}>
      <div className="px-4 pt-3 pb-2 flex items-center justify-between gap-2">
        <div className="font-semibold text-[15px]">{title}{sub != null && <span className="text-muted-foreground font-normal text-sm"> · {sub}</span>}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Chips({ options, onTap, disabled, testId }) {
  if (!options?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={testId}>
      {options.map((o) => (
        <Button key={o.key} size="sm" variant="outline" className="h-7 px-2.5 text-xs" disabled={disabled} onClick={() => onTap(o.key)} data-testid={`${testId}-${o.key}`}>{o.label}</Button>
      ))}
    </div>
  );
}

/**
 * The chat thread itself — shared by the tab and the floating button (`components/CaAgentChat.js`). Loads the
 * latest messages, polls for new ones every 20 s, marks them read, sends typed lines and taps. `fill` makes it
 * stretch to its parent (the floating panel); `onActivity` lets the parent refresh its badge after a send / tap.
 *
 * @param {{ token: string, fill?: boolean, onActivity?: Function }} props
 */
export function ChatThread({ token, fill = false, onActivity }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState('');
  const bottomRef = useRef(null);
  const cursorRef = useRef(null);

  const loadMessages = useCallback(async (since) => {
    const r = await axios.get(`${API()}/messages`, { headers, params: since ? { since, limit: 100 } : { limit: 60 } });
    const rows = r.data.messages || [];
    if (since) { if (rows.length) setMessages((m) => [...m, ...rows.filter((x) => !m.some((y) => y.id === x.id))]); }
    else setMessages(rows);
    if (r.data.cursor) cursorRef.current = r.data.cursor;
    if (rows.length) axios.post(`${API()}/messages/read`, { up_to: r.data.cursor }, { headers }).catch(() => {});
  }, [headers]);

  useEffect(() => {
    loadMessages(null).catch(() => {});
    const t = setInterval(() => { loadMessages(cursorRef.current).catch(() => {}); }, POLL_MS);
    return () => clearInterval(t);
  }, [loadMessages]);

  useEffect(() => { bottomRef.current?.scrollIntoView?.({ block: 'nearest' }); }, [messages.length]);

  const act = async (key, fn) => {
    setBusy(key);
    try { await fn(); await loadMessages(cursorRef.current); onActivity?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'That did not go through'); }
    finally { setBusy(''); }
  };
  const send = () => {
    const t = text.trim();
    if (!t) return;
    setText('');
    act('send', async () => { await axios.post(`${API()}/messages`, { text: t }, { headers }); });
  };
  const tap = (m, key) => act(`tap:${m.id}`, async () => { await axios.post(`${API()}/messages/${m.id}/tap`, { option_key: key }, { headers }); });

  return (
    <>
      <div className={`px-4 pb-2 overflow-y-auto space-y-2 ${fill ? 'flex-1 min-h-0 pt-3' : 'max-h-[420px]'}`} data-testid="ca-messages">
        {messages.length === 0 && <p className="text-sm text-muted-foreground">No messages yet. Type below, or press Run now.</p>}
        {messages.map((m) => (
          <div key={m.id} className={`text-sm rounded-lg px-3 py-2 ${m.role === 'owner' ? 'bg-[hsl(30_6%_95%)] ml-10' : 'border mr-10'}`} data-testid={`ca-msg-${m.role}`}>
            <div className="text-[11px] text-muted-foreground mb-0.5">{messageMeta(m)}</div>
            <div className="whitespace-pre-wrap">{m.text}</div>
            {m.pr_url && <a className="text-xs text-sky-700 underline" href={m.pr_url} target="_blank" rel="noreferrer">{m.pr_url}</a>}
            {(m.attachments || []).map((a, k) => (
              <a key={k} className="block text-xs text-sky-700 underline" href={`${process.env.REACT_APP_BACKEND_URL || ''}${a.url}`} target="_blank" rel="noreferrer">{a.label}</a>
            ))}
            {m.role === 'agent' && !m.answered_at && <Chips options={m.options} onTap={(k) => tap(m, k)} disabled={!!busy} testId={`ca-msg-opt-${m.id}`} />}
            {m.answered_at && m.decision?.option_key && <div className="text-[11px] text-emerald-700 mt-1">✓ {m.decision.option_key}</div>}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="px-4 pb-3 flex gap-2 border-t pt-3" style={fill ? { paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' } : undefined}>
        <Input placeholder="Type an answer or ask: what is due this week?" value={text} disabled={busy === 'send'} onChange={(e) => setText(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') send(); }} data-testid="ca-input" />
        <Button size="icon" className="h-9 w-9 shrink-0" disabled={!text.trim() || busy === 'send'} onClick={send} data-testid="ca-send">
          {busy === 'send' ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
        </Button>
      </div>
    </>
  );
}

export default function AdminCaAgent({ token, month, onAction }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [overview, setOverview] = useState(null);
  const [issues, setIssues] = useState([]);
  const [calendar, setCalendar] = useState(null);
  const [decisions, setDecisions] = useState(null);
  const [busy, setBusy] = useState('');
  const [setupOpen, setSetupOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [threadKey, setThreadKey] = useState(0);

  const load = useCallback(async () => {
    const [ov, is, cal] = await Promise.all([
      axios.get(`${API()}/overview`, { headers, params: { month } }),
      axios.get(`${API()}/issues`, { headers, params: { month } }),
      axios.get(`${API()}/calendar`, { headers }),
    ]);
    setOverview(ov.data); setIssues(is.data.issues || []); setCalendar(cal.data);
  }, [headers, month]);

  useEffect(() => { load().catch(() => toast.error('Could not load the CA agent')); }, [load]);

  const act = async (key, fn) => {
    setBusy(key);
    try { await fn(); await load(); setThreadKey((k) => k + 1); }
    catch (e) { toast.error(e?.response?.data?.detail || 'That did not go through'); }
    finally { setBusy(''); }
  };
  const answerIssue = (i, key) => act(`issue:${i.id}`, async () => { await axios.post(`${API()}/issues/${i.id}/answer`, { option_key: key }, { headers }); });
  const closeInst = (inst, state) => act(`inst:${inst.id}`, async () => { await axios.post(`${API()}/calendar/${inst.id}/close`, { state }, { headers }); });
  const runNow = () => act('tick', async () => { await axios.post(`${API()}/tick`, null, { headers, params: { force: true } }); toast.success('Agent ran'); });
  const thinkNow = () => act('think', async () => { const r = await axios.post(`${API()}/brain/run`, {}, { headers }); toast.success(r.data?.status === 'done' ? 'Shift done' : `Shift ${r.data?.status}: ${r.data?.error || ''}`); });
  const openSetup = async () => {
    try { const r = await axios.get(`${API()}/settings`, { headers }); setSettings(r.data); setSetupOpen(true); }
    catch { toast.error('Could not load the setup'); }
  };
  const saveSetup = (patch) => act('setup', async () => {
    const r = await axios.put(`${API()}/settings`, patch, { headers }); setSettings((s) => ({ ...s, ...r.data })); toast.success('Saved');
  });
  const rotateKey = () => act('key', async () => {
    const r = await axios.post(`${API()}/settings/service-key`, null, { headers });
    window.prompt('Service key — copy it now, it is shown once:', r.data.service_key);
    setSettings((s) => ({ ...s, service_key: '••••••••••' }));
  });
  const showDecisions = () => act('decisions', async () => { const r = await axios.get(`${API()}/decisions`, { headers }); setDecisions(r.data.decisions || []); });

  const today = calendar?.today || new Date().toISOString().slice(0, 10);
  const due = (calendar?.instances || []).filter((i) => ['upcoming', 'due', 'at_risk'].includes(i.state));
  const actions = issues.filter((i) => i.severity === 'action' && i.needs !== 'code');
  const codeQueue = issues.filter((i) => i.needs === 'code');
  const enabled = !!overview?.enabled;

  return (
    <div className="space-y-4" data-testid="ca-agent">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <Pill tone={enabled ? 'green' : 'grey'} testId="ca-enabled">{enabled ? 'On' : 'Off'}</Pill>
          {overview?.last_tick_at && <span className="text-muted-foreground">last run {when(overview.last_tick_at)}</span>}
          {overview?.asking > 0 && <Pill tone="amber">{overview.asking} waiting on you</Pill>}
          {codeQueue.length > 0 && <Pill tone="sky">{codeQueue.length} for the developer agent</Pill>}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={thinkNow} disabled={!!busy} data-testid="ca-think">{busy === 'think' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" strokeWidth={1.5} /> : <Sparkles className="h-4 w-4 mr-1" strokeWidth={1.5} />}Think now</Button>
          <Button size="sm" variant="outline" onClick={runNow} disabled={!!busy} data-testid="ca-run"><PlayCircle className="h-4 w-4 mr-1" strokeWidth={1.5} />Run now</Button>
          <Button size="sm" variant="outline" onClick={openSetup} aria-label="Setup" data-testid="ca-setup"><Settings className="h-4 w-4" strokeWidth={1.5} /></Button>
        </div>
      </div>

      <Panel title="Needs you" sub={actions.length || null} testId="ca-needs">
        {actions.length === 0 && <p className="px-4 pb-3 text-sm text-muted-foreground">Nothing right now.</p>}
        {actions.map((i) => (
          <div key={i.id} className="px-4 py-2.5 border-t flex items-start justify-between gap-3" data-testid="ca-issue">
            <div className="min-w-0">
              <div className="text-sm">{i.title}</div>
              {i.detail && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{i.detail}</div>}
              <Chips options={i.question?.options} onTap={(k) => answerIssue(i, k)} disabled={!!busy} testId={`ca-issue-opt-${i.id}`} />
            </div>
            {i.action?.kind && onAction && i.action.kind !== 'calendar' && (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs shrink-0" onClick={() => onAction(i)} data-testid="ca-issue-open">Open</Button>
            )}
          </div>
        ))}
      </Panel>

      <Panel title="Chat" testId="ca-chat">
        <ChatThread key={threadKey} token={token} onActivity={() => load().catch(() => {})} />
      </Panel>

      <Panel title="Due" sub={due.length ? `next ${due.length}` : null} testId="ca-due">
        {due.length === 0 && <p className="px-4 pb-3 text-sm text-muted-foreground">Nothing due in the window.</p>}
        {due.map((inst) => (
          <div key={inst.id} className="px-4 py-2 border-t flex items-center gap-3 text-sm" data-testid="ca-due-row">
            <Pill tone={dueTone(inst, today)}>{day(inst.due_date)}</Pill>
            <div className="min-w-0 flex-1">
              <span>{inst.label}</span><span className="text-muted-foreground"> · {inst.period_label || inst.period}</span>
              {inst.amount?.value != null && <span className="ml-2 tabular-nums font-medium">{inr(inst.amount.value)}</span>}
              {inst.who && <span className="ml-2 text-xs text-muted-foreground">{WHO[inst.who]}</span>}
            </div>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={!!busy} onClick={() => closeInst(inst, inst.who === 'us' ? 'paid' : 'filed')} data-testid="ca-due-close">
              {inst.who === 'us' ? 'Paid' : 'Filed'}
            </Button>
          </div>
        ))}
      </Panel>

      <Panel title="Decisions" right={decisions == null ? <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={showDecisions} data-testid="ca-decisions-show">Show</Button> : null} testId="ca-decisions">
        {decisions != null && (decisions.length === 0 ? <p className="px-4 pb-3 text-sm text-muted-foreground">None yet.</p> : decisions.slice(0, 50).map((d) => (
          <div key={d.id} className="px-4 py-1.5 border-t text-xs flex gap-2">
            <span className="text-muted-foreground shrink-0">{when(d.decision?.at)}</span><span className="truncate">{d.title}</span>
            <span className="ml-auto shrink-0 text-muted-foreground">{d.decision?.option_key || d.decision?.kind}</span>
          </div>
        )))}
      </Panel>

      <Sheet open={setupOpen} onOpenChange={setSetupOpen}>
        <SheetContent className="overflow-y-auto w-full sm:max-w-lg">
          <SheetHeader><SheetTitle>CA agent · setup</SheetTitle></SheetHeader>
          {settings && <SetupForm settings={settings} onSave={saveSetup} onRotateKey={rotateKey} busy={!!busy} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/** The setup sheet body — edits a local copy, one Save. */
export function SetupForm({ settings, onSave, onRotateKey, busy }) {
  const [s, setS] = useState(settings);
  useEffect(() => setS(settings), [settings]);
  const upd = (path, v) => setS((cur) => {
    const next = JSON.parse(JSON.stringify(cur));
    const parts = path.split('.');
    let o = next;
    for (const p of parts.slice(0, -1)) { o[p] = o[p] || {}; o = o[p]; }
    o[parts[parts.length - 1]] = v;
    return next;
  });
  const save = () => onSave({
    enabled: s.enabled, owner: s.owner, ca: s.ca, consultant: s.consultant, slots: s.slots, nudge: s.nudge, autonomy: s.autonomy,
    obligations: s.obligations, claude: { model: s.claude?.model || '' }, connectors: s.connectors,
    brain: { enabled: !!s.brain?.enabled, chat: s.brain?.chat !== false, model: s.brain?.model || '', auto_confidence: Number(s.brain?.auto_confidence || 0.9) },
  });
  const Row = ({ label, children }) => <div className="grid grid-cols-[140px_1fr] items-center gap-2 text-sm"><Label className="text-xs text-muted-foreground">{label}</Label>{children}</div>;
  const Three = ({ value, onChange }) => (
    <Select value={value || 'ask'} onValueChange={onChange}><SelectTrigger className="h-8 w-[110px]"><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="off">Off</SelectItem><SelectItem value="ask">Ask me</SelectItem><SelectItem value="auto">On its own</SelectItem></SelectContent></Select>
  );
  return (
    <div className="space-y-4 mt-4" data-testid="ca-setup-form">
      <Row label="Agent on"><Switch checked={!!s.enabled} onCheckedChange={(v) => upd('enabled', v)} data-testid="ca-setup-enabled" /></Row>
      <Row label="Your WhatsApp"><Input value={s.owner?.phone || ''} onChange={(e) => upd('owner.phone', e.target.value)} placeholder="10 digits" /></Row>
      <Row label="Nudge template"><Input value={s.nudge?.whatsapp_template || ''} onChange={(e) => upd('nudge.whatsapp_template', e.target.value)} placeholder="approved DotPe template name" /></Row>
      <Row label="CA email"><Input value={s.ca?.email || ''} onChange={(e) => upd('ca.email', e.target.value)} /></Row>
      <Row label="Consultant email"><Input value={s.consultant?.email || ''} onChange={(e) => upd('consultant.email', e.target.value)} /></Row>
      <Row label="Morning at"><Input value={s.slots?.morning_at || ''} onChange={(e) => upd('slots.morning_at', e.target.value)} className="w-24" /></Row>
      <Row label="Cashfree by API"><Switch checked={!!s.connectors?.cashfree_api?.enabled} onCheckedChange={(v) => upd('connectors.cashfree_api.enabled', v)} /></Row>
      <div className="text-xs font-semibold mt-2">The brain</div>
      <Row label="Thinks on its own"><Switch checked={s.brain?.enabled !== false} onCheckedChange={(v) => upd('brain.enabled', v)} data-testid="ca-setup-brain" /></Row>
      <Row label="Answers the chat"><Switch checked={s.brain?.chat !== false} onCheckedChange={(v) => upd('brain.chat', v)} /></Row>
      <Row label="Model"><Input value={s.brain?.model || ''} onChange={(e) => upd('brain.model', e.target.value)} placeholder="claude-sonnet-5-5" /></Row>
      <Row label="Auto only above"><Input value={s.brain?.auto_confidence ?? 0.9} onChange={(e) => upd('brain.auto_confidence', e.target.value)} className="w-24" placeholder="0.9" /></Row>
      <div className="text-xs font-semibold mt-2">Zoho entries</div>
      {(s.playbook_keys || Object.keys(s.autonomy?.playbooks || {})).map((k) => (
        <Row key={k} label={PLAYBOOK_LABEL[k] || k}><Three value={s.autonomy?.playbooks?.[k]} onChange={(v) => upd(`autonomy.playbooks.${k}`, v)} /></Row>
      ))}
      <div className="text-xs font-semibold mt-2">Mails</div>
      {(s.mail_types || Object.keys(s.autonomy?.mails || {})).map((k) => (
        <Row key={k} label={MAIL_LABEL[k] || k}><Three value={s.autonomy?.mails?.[k]} onChange={(v) => upd(`autonomy.mails.${k}`, v)} /></Row>
      ))}
      <div className="text-xs font-semibold mt-2">Obligations</div>
      {(s.registry || []).map((o) => (
        <div key={o.code} className="flex items-center gap-2 text-sm">
          <Switch checked={s.obligations?.[o.code]?.enabled !== false} onCheckedChange={(v) => upd(`obligations.${o.code}.enabled`, v)} />
          <span className="flex-1">{o.label}</span>
          <Select value={s.obligations?.[o.code]?.who || o.who} onValueChange={(v) => upd(`obligations.${o.code}.who`, v)}>
            <SelectTrigger className="h-7 w-[120px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="us">We pay</SelectItem><SelectItem value="ca">CA</SelectItem><SelectItem value="consultant">Consultant</SelectItem></SelectContent>
          </Select>
        </div>
      ))}
      <div className="text-xs font-semibold mt-2">Other agents</div>
      <Row label="Service key"><Button size="sm" variant="outline" onClick={onRotateKey} disabled={busy} data-testid="ca-rotate-key">{s.service_key ? 'Rotate' : 'Create'}</Button></Row>
      <Row label="Claude model"><Input value={s.claude?.model || ''} onChange={(e) => upd('claude.model', e.target.value)} placeholder="default" /></Row>
      <div className="flex justify-end"><Button size="sm" onClick={save} disabled={busy} data-testid="ca-setup-save">Save</Button></div>
    </div>
  );
}
